import { addDays, format } from 'date-fns';
import { selectItemType } from '@src/types';

type ContractorRequiredFields = {
  name?: string | null;
  tin?: string | null;
  npi?: string | null;
  physicalAddress?: { id?: unknown } | null;
  dispatchPlatform?: { id?: unknown } | null;
  email?: string | null;
  primaryPhone?: string | null;
};

export const validations = {
  required: (fieldName: string) => `${fieldName} is required`,
  digitsOnly: (value: string = '') => value.replace(/\D/g, ''),
  isNpiValid: (value: string = '', isRequired: boolean = true) =>
    !isRequired || /^\d{10}$/.test(value),
  isContractorInfoRequiredFieldsFilled: (
    values: ContractorRequiredFields,
    isNpiRequired: boolean,
    isDispatchPlatformRequired = true
  ) => {
    const digitsOnly = validations.digitsOnly;
    const isNpiValid = validations.isNpiValid(values.npi || '', isNpiRequired);

    return Boolean(
      values.name?.trim() &&
        digitsOnly(values.tin || '').length === 9 &&
        isNpiValid &&
        values.physicalAddress &&
        typeof values.physicalAddress === 'object' &&
        values.physicalAddress.id &&
        (!isDispatchPlatformRequired ||
          (typeof values.dispatchPlatform === 'object' &&
            values?.dispatchPlatform?.id)) &&
        values.email?.trim() &&
        digitsOnly(values.primaryPhone || '').length === 10
    );
  },
  selectRequired: (fieldName: string) => (value: selectItemType | null) => {
    return !value || value?.label === '' || value?.value === ''
      ? `${fieldName} is required`
      : true;
  },
  checkValidation: (isValid: boolean) => () => isValid,
  email: (fieldName: string) => {
    const local = "a-zA-Z0-9!#$%&'*+\\-/=?^_`{|}~";

    const pattern = new RegExp(
      `^(?=.{1,254}$)[${local}]+(?:\\.[${local}]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\\.[a-zA-Z0-9-]{2,63}$`
    );
    const message = `${fieldName} must be a valid email address`;

    return {
      pattern,
      message,
      validate: (value: string) => !value || pattern.test(value) || message,
    };
  },

  minLength: (
    fieldName: string,
    value: string = '',
    minLength: number,
    customText: string = ''
  ) => {
    if (value.length >= minLength) return;
    return customText
      ? customText
      : `${fieldName} must be at least ${minLength} characters long`;
  },

  length: (
    fieldName: string | number,
    value: string | null,
    length: number
  ) => {
    let newValue = value?.replaceAll(/[_():\s-]/g, '').trim() || '';
    if (newValue.length === 0 || newValue.length === length) return;
    return `${fieldName} must be exactly ${length} characters long`;
  },

  maxlength: (fieldName: string, value: string = '', length: number) => {
    if (value.length === 0 || value.length <= length) return;
    return `${fieldName} must maximum ${length} characters long`;
  },

  isNumber: (fieldName: string) => (value: string | number) =>
    !isNaN(Number(value)) || `${fieldName} must be a valid number`,

  time: (fieldName: string, variant: 4 | 6 = 6) => {
    if (variant === 4) {
      return {
        pattern: /^([01]\d|2[0-3]):([0-5]\d)$/,
        message: `Invalid ${fieldName} format. Please use HH:MM`,
        length: 5,
      };
    } else {
      return {
        pattern: /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/,
        message: `Invalid ${fieldName} format. Please use HH:MM:SS`,
        length: 8,
      };
    }
  },

  onlyNumberAndComma: (fieldName: string) => ({
    pattern: /^[0-9,]+$/,
    message: `${fieldName} must include only number digits`,
  }),
  notEmpty: (fieldName: string, arr: Array<any>) => {
    return Array.isArray(arr) && arr.length > 0
      ? true
      : `The ${fieldName} must have at least one element.`;
  },
  isHeight: (fieldName: string) => ({
    pattern: /^(?:\d*'?\d*)$/,
    message: `${fieldName} must include only numbers and at most one apostrophe ('), in the correct format.`,
  }),
  isWeight: (fieldName: string) => ({
    pattern: /^(?:\d*'?\d*)$/,
    message: `${fieldName} must contain only numbers.`,
  }),
  notToLateThan:
    (fieldName: string, days: number) =>
    (value: string = '') => {
      const maxDate = format(addDays(new Date(), days), 'MM/dd/yyyy');

      return value
        ? new Date(value) > new Date(maxDate)
          ? `The ${fieldName} cannot be later than ${days} days from today`
          : true
        : true;
    },
};
